import { produce } from 'immer'

import { DeveloperContextType } from '@flowx/designer/modules/ai/agents/developer/store/developer.actions'
import {
  ActionAgent,
  ActionTarget,
  ContextData,
  ConversationOutcomeClassification,
  ConversationOutcomeMultiple,
  ConversationOutcomeType,
  ConversationThreadContextDataItemType,
  ConversationThreadExecutionPlanActionTargetType,
  Message,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import {
  ActionProgressEvent,
  ActionProgressType,
} from '@flowx/designer/modules/ai/common/api/conversation-stream.types'

import { AgentAction, AgentName } from './ai-conversation.commons'
import {
  AiDeveloperContext,
  CodeSelectionContext,
  CustomComponentAiContext,
  ExplainBusinessRuleContext,
  FixCodeWarningsContext,
  ReusableFunctionContext,
  ScriptNodeContext,
} from './ai-conversation.dictionary'

const runtimeStringFromUseGraalVM = (useGraalVM: boolean): string =>
  useGraalVM ? 'GraalVM' : 'Native'

const generateBusinessRuleContextData = (
  context: AiDeveloperContext,
  useGraalVM: boolean
): ContextData => ({
  items: [
    {
      key: ContextDataKeys.ProgrammingLanguage,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.language,
    },
    {
      key: ContextDataKeys.Code,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.code,
    },
    {
      key: ContextDataKeys.DataSource,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: JSON.stringify(context.datasource),
    },
    {
      key: ContextDataKeys.UiAiContentType,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: AIContentType.BusinessRule,
    },
    {
      key: ContextDataKeys.ScriptExecutionRuntime,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: runtimeStringFromUseGraalVM(useGraalVM),
    },
  ],
})

const fixCodeWarningsContextData = (context: FixCodeWarningsContext): ContextData => ({
  items: [
    {
      key: ContextDataKeys.ProgrammingLanguage,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.language,
    },
    {
      key: ContextDataKeys.Code,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.code,
    },
    {
      key: ContextDataKeys.CodeErrors,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.warnings.map((warning) => warning.message).join('\n'),
    },
    {
      key: ContextDataKeys.UiFixCodeType,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.fixCodeType,
    },
    {
      key: ContextDataKeys.UiAiContentType,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.aiContentType,
    },
  ],
})

const explainContextData = (context: AiDeveloperContext): ContextData => ({
  items: [
    {
      key: ContextDataKeys.ProgrammingLanguage,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.language,
    },
    {
      key: ContextDataKeys.Code,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.code,
    },
    {
      key: ContextDataKeys.UiAiContentType,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.aiContentType,
    },
  ],
})

const explainBusinessRuleContextData = (context: ExplainBusinessRuleContext): ContextData => ({
  ...explainContextData({
    ...context,
    aiContentType: AIContentType.BusinessRule,
  }),
})

const generateExpressionContextData = (context: AiDeveloperContext): ContextData => ({
  items: [
    {
      key: ContextDataKeys.ProgrammingLanguage,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.language,
    },
    {
      key: ContextDataKeys.DataSource,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: JSON.stringify(context.datasource),
    },
    {
      key: ContextDataKeys.UiAiContentType,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: AIContentType.Expression,
    },
  ],
})

const generateComputedValueContextData = (context: AiDeveloperContext): ContextData => ({
  items: [
    {
      key: ContextDataKeys.ProgrammingLanguage,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.language,
    },
    {
      key: ContextDataKeys.DataSource,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: JSON.stringify(context.datasource),
    },
    {
      key: ContextDataKeys.UiAiContentType,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: AIContentType.ComputedValue,
    },
  ],
})

const codeSelectionContextData = (context: CodeSelectionContext): ContextData => ({
  items: [
    {
      key: ContextDataKeys.ProgrammingLanguage,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.language,
    },
    {
      key: ContextDataKeys.Code,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.code,
    },
    {
      key: ContextDataKeys.CodeSelected,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.selection.selected,
    },
    {
      key: ContextDataKeys.DataSource,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: JSON.stringify(context.datasource),
    },
    {
      key: ContextDataKeys.UiSelectionStart,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.selection.startLineNumber.toString(),
    },
    {
      key: ContextDataKeys.UISelectionEnd,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.selection.endLineNumber.toString(),
    },
    {
      key: ContextDataKeys.UiAiContentType,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.aiContentType,
    },
  ],
})

const improveCodeContextData = (context: CodeSelectionContext): ContextData =>
  codeSelectionContextData(context)

const addCommentContextData = (context: CodeSelectionContext): ContextData =>
  codeSelectionContextData(context)

const generateCustomComponentContextData = (context: CustomComponentAiContext): ContextData => ({
  items: [
    {
      key: ContextDataKeys.Code,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: JSON.stringify({
        jsx: context.sourceCode ?? '',
        css: context.styles ?? '',
        packageJson: context.packageJson ?? '',
      }),
    },
    {
      key: ContextDataKeys.ComponentMetadata,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: JSON.stringify({
        name: context.componentName,
        uniqueIdentifier: context.uniqueIdentifier,
        available_libraries: context.availableLibraries,
        jsx_filename: context.jsxFilename,
        css_filename: context.cssFilename,
      }),
    },
    {
      key: ContextDataKeys.DataSource,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: JSON.stringify(context.datasource),
    },
    {
      key: ContextDataKeys.UiAiContentType,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: AIContentType.CustomComponent,
    },
  ],
})

export enum MainOutcomeAction {
  ImproveCode = 'improve_code',
  DocumentCode = 'document_code',
  FixCode = 'fix_code',
  Expression = 'expression',
  ComputedValue = 'computed',
  BusinessRule = 'business_rule',
  EditBusinessRule = 'edit_business_rule',
  CustomComponent = 'custom_component',
  ReusableFunction = 'reusable_function',
  EditReusableFunction = 'edit_reusable_function',
}

export enum ContextDataKeys {
  ProgrammingLanguage = 'PROGRAMMING_LANGUAGE',
  Code = 'CODE',
  CodeSelected = 'CODE_SELECTED',
  DataSource = 'DATASOURCE',
  UiSelectionStart = 'UI_SELECTION_START',
  UISelectionEnd = 'UI_SELECTION_END',
  CodeErrors = 'CODE_ERRORS',
  UiAgent = 'UI_AGENT',
  UiTarget = 'UI_TARGET',
  UiFixCodeType = 'UI_FIX_CODE_TYPE',
  AiProcessContext = 'PROCESS_CONTEXT',
  UiAiContentType = 'UI_AI_CONTENT_TYPE',
  UIMessageType = 'UI_MESSAGE_TYPE',
  ProcessDefinition = 'PROCESS_DEFINITION',
  ProcessNodes = 'PROCESS_NODES',
  UiTemplateDefinition = 'UI_TEMPLATE_DEFINITION',
  UiTemplateResource = 'UI_TEMPLATE_RESOURCE',
  UiFlowResource = 'UI_FLOW_RESOURCE',
  Process = 'process',
  ProcessReference = 'process_reference',
  ReferencedNode = 'referenced_node',
  ReferencedAction = 'referenced_action',
  UIAiAnalystSummaryChanges = 'UI_AI_ANALYST_SUMMARY_CHANGES',
  UIAiDesignerSummaryChanges = 'UI_AI_DESIGNER_SUMMARY_CHANGES',
  UIAiDesignerRepairLog = 'UI_AI_DESIGNER_REPAIR_LOG',
  DesignerDataModel = 'DESIGNER_DATA_MODEL',
  UiAssignPagesToUserTasks = 'UI_ASSIGN_PAGES_TO_USER_TASKS',
  UiDestinationSelector = 'UI_DESTINATION_SELECTOR',
  DesignerReferenceImage = 'DESIGNER_REFERENCE_IMAGE',
  ProcessReferenceImage = 'PROCESS_REFERENCE_IMAGE',
  ProcessReferenceImageType = 'PROCESS_REFERENCE_IMAGE_TYPE',
  DesignerReferenceDocument = 'DESIGNER_REFERENCE_DOCUMENT',
  ProcessStartNodeId = 'PROCESS_START_NODE_ID',
  ProcessEndNodeId = 'PROCESS_END_NODE_ID',
  ThreadCausedByAgentPlan = 'THREAD_CAUSED_BY_AGENT_PLAN',
  GeneratedDataModel = 'GENERATED_DATA_MODEL',
  DataModelContext = 'DATA_MODEL_CONTEXT',
  ComponentMetadata = 'COMPONENT_METADATA',
  UIAiDeveloperDependencySummary = 'UI_AI_DEVELOPER_DEPENDENCY_SUMMARY',
  FunctionMetadata = 'FUNCTION_METADATA',
  ScriptExecutionRuntime = 'SCRIPT_EXECUTION_RUNTIME',
  ProjectDataModel = 'PROJECT_DATA_MODEL',
  GenerateResponseSchema = 'GENERATE_SCHEMA',
  ExistingInstructions = 'EXISTING_INSTRUCTIONS',
  ExistingContext = 'EXISTING_CONTEXT',
  ExistingSchema = 'EXISTING_SCHEMA',
  UiNodeConfigDraft = 'UI_NODE_CONFIG_DRAFT',
  DesignerContext = 'DESIGNER_CONTEXT',
  UiFlowSelectedAreaKey = 'DESIGNER_SELECTED_AREA_KEY',
}

export enum BackendContextDataKeys {
  PlannerTarget = '__PLANNER__TARGET__',
}

export enum TargetNodeType {
  ServiceTask = 'SERVICE_TASK',
}

export enum AIContentType {
  BusinessRule = 'Business Rule',
  Expression = 'Expression',
  ComputedValue = 'Computed Value',
  Process = 'Process',
  Ui = 'UI',
  CustomComponent = 'Custom Component',
  ReusableFunction = 'Reusable Function',
}

export enum OutcomeType {
  // Analyst outcomes
  Process = 'Process',

  // Designer outcomes
  UI = 'UI',
  DataModel = 'Data Model',

  // Developer outcomes
  BusinessRule = 'Business Rule',
  Expression = 'Expression',
  ComputedValue = 'Computed Value',
  CustomComponent = 'Custom Component',
  ReusableFunction = 'Reusable Function',
}

export enum MessageType {
  SystemMsg = 'system',
  ErrorMsg = 'error',
  AiMsg = 'ai',
  UserMsg = 'user',
}

const DeveloperContextToAiContentTypeMap: Record<DeveloperContextType, AIContentType> = {
  [DeveloperContextType.BusinessRule]: AIContentType.BusinessRule,
  [DeveloperContextType.Expression]: AIContentType.Expression,
  [DeveloperContextType.ComputedValue]: AIContentType.ComputedValue,
  [DeveloperContextType.ReusableFunction]: AIContentType.ReusableFunction,
}

const getAiContentTypeFromDeveloperContextType = (
  contextType: DeveloperContextType
): AIContentType => {
  const aiContentType = DeveloperContextToAiContentTypeMap[contextType]

  if (!aiContentType) {
    throw new Error(`Invalid context type: ${contextType}`)
  }

  return aiContentType
}

// Agents whose thread outcome is the last message's outcome, keyed to the actions that may
// produce one. `null` means any action of that agent qualifies.
const PassthroughOutcomeActionsByAgent: Partial<Record<AgentName, readonly AgentAction[] | null>> =
  {
    [AgentName.AiDesigner]: [
      AgentAction.CreateUi,
      AgentAction.CreateUiFromImage,
      AgentAction.EditUi,
      AgentAction.CreateUiTemplate,
      AgentAction.GenerateUiFlow,
      AgentAction.EditUiFlow,
    ],
    [AgentName.AiAnalyst]: [
      AgentAction.CreateProcess,
      AgentAction.CreateProcessFromImage,
      AgentAction.CreateProcessFromBpmn,
      AgentAction.EditProcess,
    ],
    [AgentName.AiAssistant]: null,
    [AgentName.AiArchitect]: [AgentAction.GenerateDataModel, AgentAction.ExtendDataModel],
  }

const mapMessageOutcomeToThreadOutcome = (
  updates: ActionProgressEvent,
  lastMessage: Message
): ConversationOutcomeMultiple => {
  if (updates.agent.agentId === AgentName.AiDeveloper) {
    return handleAiDeveloperOutcome(updates, lastMessage)
  }

  const allowedActions = PassthroughOutcomeActionsByAgent[updates.agent.agentId as AgentName]
  if (allowedActions === null || allowedActions?.includes(updates.agent.actionId as AgentAction)) {
    return lastMessage.outcome
  }

  throw new Error('Unexpected agent or action id when updating thread outcome')
}

export const ActionProgressTypeToOutcomeClassificationMap: Record<
  ActionProgressType,
  ConversationOutcomeClassification
> = {
  UNKNOWN: ConversationOutcomeClassification.Unknown,
  MAIN_OUTCOME: ConversationOutcomeClassification.Main,
  CHAT: ConversationOutcomeClassification.Chat,
  INTERRUPT: ConversationOutcomeClassification.Interrupt,
  LOGGING: ConversationOutcomeClassification.Logging,
  REASONING: ConversationOutcomeClassification.Reasoning,
  ERROR: ConversationOutcomeClassification.Error,
}

const mergeCodeSelectionIntoOutcome = (message: Message): ConversationOutcomeMultiple | null => {
  const action = message.executionPlan?.actions?.[0]?.agent?.action
  if (action !== AgentAction.ImproveCode && action !== AgentAction.DocumentCode) {
    return null
  }

  const codeOutcomeIndex = message.outcome?.outcomes?.findIndex(
    (outcome) =>
      outcome.outcomeType === ConversationOutcomeType.Textual &&
      outcome.outcomeClassification === ConversationOutcomeClassification.Main
  )

  if (codeOutcomeIndex == null || codeOutcomeIndex === -1) {
    return null
  }

  const selectionStartStr = message.contextData?.items?.find(
    (item) => item.key === ContextDataKeys.UiSelectionStart
  )?.valueGenericString
  const selectionEndStr = message.contextData?.items?.find(
    (item) => item.key === ContextDataKeys.UISelectionEnd
  )?.valueGenericString
  const codeStr = message.contextData?.items?.find(
    (item) => item.key === ContextDataKeys.Code
  )?.valueGenericString

  if (!selectionStartStr || !selectionEndStr || !codeStr) {
    return null
  }

  const selectionStart = parseInt(selectionStartStr, 10)
  const selectionEnd = parseInt(selectionEndStr, 10)
  const code = codeStr.split('\n')
  const improvedCode = message.outcome.outcomes[codeOutcomeIndex].textual.content.split('\n')

  return produce(message.outcome, (draft) => {
    draft.outcomes[codeOutcomeIndex].textual.content = [
      ...code.slice(0, selectionStart - 1),
      ...improvedCode,
      ...code.slice(selectionEnd),
    ].join('\n')
  })
}

const handleAiDeveloperOutcome = (
  updates: ActionProgressEvent,
  lastMessage: Message
): ConversationOutcomeMultiple => {
  if (
    [AgentAction.ImproveCode, AgentAction.DocumentCode].includes(
      updates.agent.actionId as AgentAction
    )
  ) {
    const merged = mergeCodeSelectionIntoOutcome(lastMessage)
    if (merged) {
      return merged
    }
  }

  return lastMessage.outcome
}

const generateReusableFunctionContextData = (
  context: ReusableFunctionContext,
  useGraalVM: boolean
): ContextData => ({
  items: [
    {
      key: ContextDataKeys.ProgrammingLanguage,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.language,
    },
    {
      key: ContextDataKeys.Code,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.code,
    },
    {
      key: ContextDataKeys.DataSource,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: JSON.stringify(context.functionDataModel),
    },
    {
      key: ContextDataKeys.FunctionMetadata,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: JSON.stringify(context.functionMetadata),
    },
    {
      key: ContextDataKeys.UiAiContentType,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: AIContentType.ReusableFunction,
    },
    {
      key: ContextDataKeys.ScriptExecutionRuntime,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: runtimeStringFromUseGraalVM(useGraalVM),
    },
  ],
})

const REUSABLE_FUNCTION_HINTED_ACTION_MESSAGE =
  "I'm here to help you build reusable functions with clean, optimized, and scalable code."

interface ReusableFunctionHintedAction {
  message: string
  context: ContextData
  target: ActionTarget
  userPromptRequired: true
  agent: ActionAgent
}

const buildReusableFunctionGenerationHintedAction = (
  context: ReusableFunctionContext,
  useGraalVM: boolean
): ReusableFunctionHintedAction => ({
  message: REUSABLE_FUNCTION_HINTED_ACTION_MESSAGE,
  context: generateReusableFunctionContextData(context, useGraalVM),
  target: {
    name: context.functionMetadata.name || 'Reusable Function',
    targetType: ConversationThreadExecutionPlanActionTargetType.ReusableFunction,
  },
  userPromptRequired: true,
  agent: {
    agent: AgentName.AiDeveloper,
    action: context.code.trim() ? AgentAction.EditReusableFunction : AgentAction.ReusableFunction,
  },
})

const generateScriptNodeContextData = (
  context: ScriptNodeContext,
  scriptExecutionRuntime: string
): ContextData => ({
  items: [
    {
      key: ContextDataKeys.ProgrammingLanguage,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.language,
    },
    {
      key: ContextDataKeys.Code,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: context.code,
    },
    {
      key: ContextDataKeys.DataSource,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: JSON.stringify(context.workflowDataModel),
    },
    {
      key: ContextDataKeys.UiAiContentType,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: AIContentType.BusinessRule,
    },
    {
      key: ContextDataKeys.ScriptExecutionRuntime,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: scriptExecutionRuntime,
    },
  ],
})

const SCRIPT_NODE_HINTED_ACTION_MESSAGE =
  "I'm here to help you write clean, optimized code for your workflow script node."

interface ScriptNodeHintedAction {
  message: string
  context: ContextData
  target: ActionTarget
  userPromptRequired: true
  agent: ActionAgent
}

const buildScriptNodeGenerationHintedAction = (
  context: ScriptNodeContext,
  scriptExecutionRuntime: string
): ScriptNodeHintedAction => ({
  message: SCRIPT_NODE_HINTED_ACTION_MESSAGE,
  context: generateScriptNodeContextData(context, scriptExecutionRuntime),
  // The target is thread metadata; like the reusable-function flow it carries only a
  // name + type (no sub-descriptor) since the script-node flow is modal-driven.
  target: {
    name: 'Script Node',
    targetType: ConversationThreadExecutionPlanActionTargetType.Node,
  },
  userPromptRequired: true,
  agent: {
    agent: AgentName.AiDeveloper,
    action: context.code.trim() ? AgentAction.EditBusinessRule : AgentAction.BusinessRule,
  },
})

export const AiConversationModel = {
  generateBusinessRuleContextData,
  fixCodeWarningsContextData,
  explainBusinessRuleContextData,
  generateExpressionContextData,
  improveCodeContextData,
  addCommentContextData,
  generateComputedValueContextData,
  mapMessageOutcomeToThreadOutcome,
  explainContextData,
  getAiContentTypeFromDeveloperContextType,
  mergeCodeSelectionIntoOutcome,
  generateCustomComponentContextData,
  generateReusableFunctionContextData,
  buildReusableFunctionGenerationHintedAction,
  generateScriptNodeContextData,
  buildScriptNodeGenerationHintedAction,
}
